import { formatDistanceToNow } from 'date-fns';
import { MeasuringPointStatus, type Sensor, type SensorQuotaType } from '@/graphql/types';
import type { MeasuringPointWithProject } from '../types';
import { hasAtLeastPermission, PERMISSION, requirePermission } from '../../sharing/utils';
import { SwarmIconClasses } from '../../../../enums';
import { format } from '../../../../utils/formatting';

type OverviewSensor = Pick<
    Sensor,
    | 'batteryCharge'
    | 'batteryChargeLastUpdate'
    | 'dataQuota'
    | 'firmwareUpgradePercentage'
    | 'hasDataUsageInfo'
    | 'hasValidSubscription'
    | 'isRestarting'
    | 'lastContact'
    | 'name'
    | 'smsLimit'
    | 'smsQuota'
    | 'smsUsed'
>;

export interface ProjectMeasuringPointRowViewData {
    id: string;
    isArchived: boolean;
    cityLabel: string;
    dataQuotaWarning: SensorQuotaType | null;
    dataUsage: SensorQuotaType | null;
    deviceLabel: string;
    hasDataQuotaInfo: boolean;
    hasSmsQuotaInfo: boolean;
    hasStatusDetails: boolean;
    isDetachedDevice: boolean;
    name: string;
    batteryCharge: number | null | undefined;
    batteryIconLevel: number | null;
    batteryTooltip: string | undefined;
    lastContactRelative: string;
    lastContactTitle: string | undefined;
    renderedStatusSecondaryLabel: string | null;
    shouldRenderRestartAction: boolean;
    showSubscriptionWarning: boolean;
    smsQuotaWarning: SensorQuotaType | null;
    smsUsage: SensorQuotaType | null;
    status: MeasuringPointStatus;
    swarmIconClass: string;
    usesCustomSettingsSource: boolean;
}

const FALLBACK_STATUS: MeasuringPointStatus = MeasuringPointStatus.Inactive;

function getCityLabel(value: unknown): string {
    return typeof value === 'string' && value ? value : gettext('UNKNOWN');
}

function hasSensorDataQuotaInfo(sensor: OverviewSensor | null | undefined): boolean {
    return sensor?.hasDataUsageInfo === true;
}

function hasSensorSmsQuotaInfo(sensor: OverviewSensor | null | undefined): boolean {
    // Legacy overview always shows SMS usage details for attached sensors.
    // When no explicit limit exists, the UI falls back to "0 / ∞".
    return sensor != null;
}

function parseDate(value: string | null | undefined): Date | null {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatRelativeDate(date: Date): string {
    return formatDistanceToNow(date, { addSuffix: true });
}

function getQuotaWarning(usage: SensorQuotaType | null): SensorQuotaType | null {
    return usage?.isWarning || usage?.isLimit ? usage : null;
}

function hasStatusDetails(sensor: OverviewSensor | null | undefined): boolean {
    return hasSensorDataQuotaInfo(sensor) || hasSensorSmsQuotaInfo(sensor);
}

function getStatusSecondaryLabel(
    status: MeasuringPointStatus,
    sensor: OverviewSensor | null | undefined
): string | null {
    if (status === MeasuringPointStatus.UpdateReady) {
        return sensor?.isRestarting === true
            ? gettext('OVERVIEW_SENSOR_STATUS_RESTART_IN_PROGRESS')
            : gettext('OVERVIEW_SENSOR_STATUS_PLEASE_RESTART');
    }

    if (status === MeasuringPointStatus.DownloadingUpdate) {
        if (sensor?.firmwareUpgradePercentage == null) {
            return null;
        }

        return `${sensor.firmwareUpgradePercentage.toFixed(2)}%`;
    }

    return null;
}

function shouldShowRestartAction(
    status: MeasuringPointStatus,
    sensor: OverviewSensor | null | undefined
): boolean {
    return status === MeasuringPointStatus.UpdateReady && sensor?.isRestarting !== true;
}

function usesCustomSettingsSource(overriddenFields: MeasuringPointWithProject['overriddenFields']) {
    return Array.isArray(overriddenFields) && overriddenFields.length > 0;
}

export function buildProjectMeasuringPointRowViewData(
    measuringPoint: MeasuringPointWithProject
): ProjectMeasuringPointRowViewData {
    const { sensor } = measuringPoint;
    const isArchived = Boolean(measuringPoint.archived);
    const hasDataQuotaInfo = hasSensorDataQuotaInfo(sensor);
    const hasSmsQuotaInfo = hasSensorSmsQuotaInfo(sensor);
    const dataUsage = sensor?.dataQuota ?? null;
    const smsUsage = sensor?.smsQuota ?? null;

    const status = measuringPoint.status ?? FALLBACK_STATUS;
    const renderedStatusSecondaryLabel = getStatusSecondaryLabel(status, sensor);
    const shouldRenderRestartAction =
        renderedStatusSecondaryLabel != null && shouldShowRestartAction(status, sensor);

    const lastContactDate = parseDate(sensor?.lastContact);
    const lastContactRelative = lastContactDate
        ? formatRelativeDate(lastContactDate)
        : gettext('UNKNOWN');
    const lastContactTitle = lastContactDate
        ? format(lastContactDate, { useBrowserTimezone: true })
        : undefined;

    const batteryCharge = sensor?.batteryCharge;
    const batteryLastUpdate = parseDate(sensor?.batteryChargeLastUpdate);
    const batteryTooltip = batteryLastUpdate ? formatRelativeDate(batteryLastUpdate) : undefined;
    const batteryIconLevel =
        batteryCharge !== null && batteryCharge !== undefined
            ? Math.min(4, Math.max(0, Math.floor(batteryCharge / 25)))
            : null;

    const isDetachedDevice = sensor == null;
    const deviceLabel = isDetachedDevice ? gettext('UNKNOWN') : sensor.name;
    const showSubscriptionWarning =
        hasAtLeastPermission(
            requirePermission(measuringPoint.permission, `measuring point "${measuringPoint.id}"`),
            PERMISSION.FullAccess
        ) && sensor?.hasValidSubscription === false;

    return {
        id: measuringPoint.id,
        isArchived,
        cityLabel: getCityLabel(measuringPoint.city),
        dataUsage,
        deviceLabel,
        hasDataQuotaInfo,
        dataQuotaWarning: getQuotaWarning(dataUsage),
        hasSmsQuotaInfo,
        smsQuotaWarning: getQuotaWarning(smsUsage),
        hasStatusDetails: hasStatusDetails(sensor),
        isDetachedDevice,
        name: measuringPoint.name,
        batteryCharge,
        batteryIconLevel,
        batteryTooltip,
        lastContactRelative,
        lastContactTitle,
        renderedStatusSecondaryLabel,
        shouldRenderRestartAction,
        showSubscriptionWarning,
        smsUsage,
        status,
        swarmIconClass: SwarmIconClasses[measuringPoint.swarmType],
        usesCustomSettingsSource: usesCustomSettingsSource(measuringPoint.overriddenFields),
    };
}
